import { useEffect, useLayoutEffect, useRef } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useUserTokenDecoded } from '../../../redux/slices/appInfo/hooks';
import { isWorkspaceChange } from '../../../redux/slices/appInfo/isWorkspaceChange';
import { fymAPI } from '../../../redux/api/fymAPI';
import { musicAPI } from '../../../redux/api/musicAPI';
import { setPendingWorkspaceReset } from './workspaceResetState';

/**
 * The one place that drops the data of the old workspace when the session token names another
 * workspace. Every path goes through it: a switch, a join, a leave, and a refresh that falls back
 * to the home workspace after a lost membership.
 *
 * The reducer of `setUserToken` already clears the active artist. The layout effect resets both
 * RTK Query APIs before the browser paints. The passive effect resets the tRPC queries. It must
 * run after the effects of the children: each query takes the new tRPC client (with the new
 * `Authorization`) in an effect of its own render. Mount the caller after the app tree.
 */
export function useWorkspaceChangeReset(): void {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const licenseId = useUserTokenDecoded()?.licenseId;
  const lastLicenseId = useRef(licenseId);
  const shouldResetQueries = useRef(false);

  useLayoutEffect(() => {
    const previousLicenseId = lastLicenseId.current;
    lastLicenseId.current = licenseId;
    if (!isWorkspaceChange(previousLicenseId, licenseId)) {
      return;
    }
    shouldResetQueries.current = true;
    batch(() => {
      dispatch(fymAPI.util.resetApiState());
      dispatch(musicAPI.util.resetApiState());
    });
  }, [dispatch, licenseId]);

  useEffect(() => {
    if (!shouldResetQueries.current) {
      return;
    }
    shouldResetQueries.current = false;
    // Reset, not invalidate: the old workspace's data must not show while the new one loads.
    setPendingWorkspaceReset(queryClient.resetQueries().catch(console.error));
  }, [licenseId, queryClient]);
}
